import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RemovedBlock, ScheduleButtonType } from '@groundworks/ngx-groundworks-ui';
import { CalendarService } from './calendar.service';
import { CrewScheduleStore } from './crew-schedule-store.service';
import { ModalService } from './modal.service';

@Injectable({ providedIn: 'root' })
export class AvailabilityService {
  private readonly calendarService = inject(CalendarService);
  private readonly modalService = inject(ModalService);
  private readonly store = inject(CrewScheduleStore);

  addAvailability(crewGroupId: string, date: Date, branchId: string, destroyRef: DestroyRef): void {
    this.store.setDayButtonDisabled(crewGroupId, date, true);

    this.calendarService
      .createForemanAvailability(crewGroupId, date, branchId)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe({
        next: (result) => {
          if (result.created) {
            this.store.setDayButtonType(crewGroupId, date, ScheduleButtonType.REMOVE);
            this.store.setDayRemovedBlocks(crewGroupId, date, []);
          }
          this.store.setDayButtonDisabled(crewGroupId, date, false);
        },
        error: (err: unknown) => {
          const isConflict = err instanceof HttpErrorResponse && err.status === 409;
          if (isConflict) {
            console.warn(`Availability already exists (crew=${crewGroupId}, date=${date.toISOString()}, branch=${branchId})`);
            this.store.setDayButtonType(crewGroupId, date, ScheduleButtonType.REMOVE);
          } else {
            console.error(`Failed to create foreman availability (crew=${crewGroupId}, date=${date.toISOString()}, branch=${branchId}):`, err);
          }
          this.store.setDayButtonDisabled(crewGroupId, date, false);
          if (isConflict) {
            this.modalService.openInfoModal(
              'Availability Already Added',
              'This availability was already added. The calendar has been updated.',
              'bi bi-info-circle-fill',
              'alert-info border-info');
          } else {
            this.modalService.openErrorModal(
              'Unable to Add Availability',
              'An error occurred while creating the foreman availability. Please try again.');
          }
        },
      });
  }

  removeAvailability(crewGroupId: string, date: Date, branchId: string, destroyRef: DestroyRef): void {
    const reasons = this.store.deletionReasons();
    if (!reasons) {
      this.modalService.openErrorModal(
        'Unable to Remove Availability',
        'Deletion reasons could not be loaded. Please refresh the page and try again.');
      return;
    }

    const modalRef = this.modalService.openRemoveAvailabilityModal(reasons);

    modalRef.result.then(
      (reason: string) => {
        this.store.setDayButtonDisabled(crewGroupId, date, true);
        this.calendarService
          .deleteForemanAvailability(crewGroupId, date, branchId, reason)
          .pipe(takeUntilDestroyed(destroyRef))
          .subscribe({
            next: (result) => {
              this.store.setDayButtonType(crewGroupId, date, ScheduleButtonType.ADD);
              this.addRemovedBlock(crewGroupId, date, reason);
              if (!result.deleted) {
                this.modalService.openErrorModal(
                  'Unable to Remove Availability',
                  'This availability has already been removed. The calendar has been updated.');
              }
              this.store.setDayButtonDisabled(crewGroupId, date, false);
            },
            error: (err: unknown) => {
              const isNotFound = err instanceof HttpErrorResponse && err.status === 404;
              if (isNotFound) {
                this.store.setDayButtonType(crewGroupId, date, ScheduleButtonType.ADD);
              } else {
                console.error(`Failed to remove foreman availability (crew=${crewGroupId}, date=${date.toISOString()}, branch=${branchId}):`, err);
              }
              this.store.setDayButtonDisabled(crewGroupId, date, false);
              this.modalService.openErrorModal(
                'Unable to Remove Availability',
                isNotFound
                  ? 'This availability has already been removed. The calendar has been updated.'
                  : 'An error occurred while removing the foreman availability. Please try again.');
            },
          });
      },
      () => { /* User cancelled removal — no action needed, button state unchanged */ },
    );
  }

  private addRemovedBlock(foremanUserId: string, date: Date, reason: string): void {
    const day = this.store.findDay(foremanUserId, date);
    if (!day) {
      console.warn('addRemovedBlock: day not found for', foremanUserId, date);
      return;
    }
    const block: RemovedBlock = {
      id: crypto.randomUUID(),
      timeHeader: 'All Day',
      reason,
    };
    this.store.setDayRemovedBlocks(foremanUserId, date, [...(day.removedBlocks ?? []), block]);
  }
}
